import { Attachment } from '../attachments.model';
import { ComponentExpandStrategy } from '../guideline-chapter.model';
import { GuidelineComponent } from '../guideline-component.model';
import { GuidelineValidationResult } from '../GuidelineValidationResult';
import { Reference } from '../reference.model';
import { BaseEditableProperties, GuidelineWidget, Widget } from './widget.model';

export enum ComponentListType {
  Conditions = 1,
  Criteria = 2,
  DiagnosisPlan = 3,
}
export type ComponentListLocalChanges = BaseEditableProperties;

export type ComponentListWidget = Widget & {
  widgetType: 'component-list';
  widgetIds?: string[];
  componentExpandStrategyOnRead?: ComponentExpandStrategy;
  componentListType?: ComponentListType;
} & {
  localChanges?: ComponentListLocalChanges;
};

export type ComponentLocalChanges = BaseEditableProperties &
  Partial<{ deletedOriginalAttachmentIds: string[]; attachments: Attachment[]; updatedWidgetIds: string[] }>;

export type ComponentWidget<T = GuidelineComponent> = Widget & {
  widgetType: 'component';
  childrenIds: string[];
  component: T;
  widgetIds?: string[];
  updatedWidgetIds?: string[];
} & {
  localChanges?: ComponentLocalChanges;
};

export const createComponentListWidget = (overrides?: Partial<Omit<ComponentListWidget, 'widgetType'>>): ComponentListWidget => ({
  widgetType: 'component-list',
  uniqueId: crypto.randomUUID(),
  references: [],
  attachments: [],
  links: [],
  widgetIds: [],
  componentExpandStrategyOnRead: ComponentExpandStrategy.Default,
  ...overrides,
});

export const createComponentWidget = <T = GuidelineComponent>(
  overrides?: Partial<Omit<ComponentWidget<T>, 'widgetType'>>,
): ComponentWidget<T> =>
  ({
    widgetType: 'component',
    uniqueId: crypto.randomUUID(),
    references: [],
    links: [],
    attachments: [],
    childrenIds: [],
    widgetIds: [],
    ...overrides,
  }) as ComponentWidget<T>;

export const getReferencesFromComponentListWidget = (widget: ComponentListWidget, widgetMap: Record<string, Widget>): Reference[] => {
  const getReferencesFromComponentWidgetRecursively = (widgetId: string): Reference[] => {
    const widget = widgetMap[widgetId];
    if (!widget || widget.widgetType !== 'component') {
      return [];
    }

    const componentWidget = widget as ComponentWidget;
    const references: Reference[] = [...(componentWidget.references ?? [])];

    componentWidget.widgetIds?.forEach((id: string) => {
      const widget = widgetMap[id];
      if (!widget || widget.widgetType !== 'text') {
        return;
      }

      references.push(...(widget.references ?? []));
    });

    for (const childId of componentWidget.childrenIds ?? []) {
      references.push(...getReferencesFromComponentWidgetRecursively(childId));
    }

    return references;
  };

  const widgetRefs = widget.references ?? [];
  const componentRefs = (widget.widgetIds ?? []).flatMap(getReferencesFromComponentWidgetRecursively);

  return [...widgetRefs, ...componentRefs];
};

export const validateComponentListWidget = (
  widget: ComponentListWidget,
  chapter: { title?: string },
  widgetMap: Record<string, GuidelineWidget>,
): GuidelineValidationResult => {
  if (!widget.widgetIds || widget.widgetIds.length === 0) {
    return {
      valid: true,
      message: '',
    };
  }

  const componentWidgets: ComponentWidget[] = [];

  const collect = (id: string): void => {
    const widget = widgetMap[id];
    if (!widget || widget.widgetType !== 'component') {
      return;
    }

    componentWidgets.push(widget);

    const componentWidget = widget as ComponentWidget;
    (componentWidget.childrenIds ?? []).forEach(collect);
  };

  widget.widgetIds.forEach(collect);

  for (const componentWidget of componentWidgets) {
    if (!componentWidget.component.title) {
      return {
        valid: false,
        message: 'NormalChapterHasInvalidComponents',
        params: { chapter: chapter.title },
      };
    }
  }

  return {
    valid: true,
    message: '',
  };
};
